"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import profileIcon from "../../asset/profile_icon.png"
import { AppSessionService } from "../../infrastructure/services/session"
import type { Terminal, User } from "../../infrastructure/models/user"
import type { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"
import { COLORS } from "../constants/colors"
import { AppApiService } from "../../infrastructure/services/api"

const MyAccountPage: React.FC = () => {
    const sessionService = new AppSessionService()
    const apiService = new AppApiService()
    const user: User = sessionService.user
    const [franchiseName] = useState<string>(sessionService.franchiseName || "No Franchise")
    const terminals: Terminal[] = sessionService.terminals || []

    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [retypePassword, setRetypePassword] = useState("")
    const [loading, setLoading] = useState(false)

    const [message, setMessage] = useState<{ text: string, type: "success" | "error" | "" }>({
        text: "",
        type: "",
    })

    const getRoleName = (role: FranchiseRole): string => {
        const roles = ["OWNER", "ADMIN", "MANAGER", "STAFF"]
        return roles[role] || "USER"
    }

    const handleChangePassword = async () => {
        if (!user) {
            setMessage({ text: "No user data available", type: "error" })
            return
        }

        if (!currentPassword || !newPassword || !retypePassword) {
            setMessage({ text: "Please fill in all fields.", type: "error" })
            return
        }

        if (newPassword !== retypePassword) {
            setMessage({ text: "New passwords do not match.", type: "error" })
            return
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,24}$/

        if (!passwordRegex.test(newPassword)) {
            setMessage({
                text: "Password must be 8–24 characters long, include uppercase, lowercase, number, and special character.",
                type: "error",
            })
            return
        }

        if (currentPassword === newPassword) {
            setMessage({
                text: "New password must be different from current password.",
                type: "error",
            })
            return
        }

        try {
            setLoading(true)
            setMessage({ text: "", type: "" })

            const response = await apiService.changePassword(currentPassword, newPassword)
            if (response) {
                setMessage({ text: "Password changed successfully.", type: "success" })
                setCurrentPassword("")
                setNewPassword("")
                setRetypePassword("")
            } else {
                setMessage({
                    text: "Failed to change password.",
                    type: "error",
                })
            }
        } catch (error: any) {
            setMessage({
                text: error?.message || "Something went wrong.",
                type: "error",
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ text: "", type: "" }), 4000)
            return () => clearTimeout(timer)
        }
    }, [message])

    if (!user) {
        return (
            <div className="flex items-center justify-center h-screen overflow-x-auto">
                <p className="text-lg font-semibold text-gray-700">No user data available</p>
            </div>
        )
    }

    return (
        <div className="pt-2 px-2 pb-6 w-full max-w-full">
            <div
                className="bg-white shadow-md rounded-lg w-full p-8 overflow-y-auto max-h-[80vh]"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
                <style>{`
          div::-webkit-scrollbar { display: none }
        `}</style>

                {/* Profile Section */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-5 mb-8 pb-6 border-b border-gray-200">
                    <img
                        src={user?.profileImage || profileIcon}
                        alt="Profile"
                        className="w-20 h-20 rounded-full border border-gray-200"
                    />
                    <div className="mt-3 sm:mt-0 text-center sm:text-left">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {user?.firstname || "User"} {user?.lastname || ""}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">{getRoleName(user?.role)}</p>
                    </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
                    <Detail label="First Name" value={user?.firstname} />
                    <Detail label="Last Name" value={user?.lastname} />
                    <Detail label="Email" value={user?.email} />
                    <Detail label="Phone" value={user?.phone?.primary} />
                    <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Status</p>
                        <span
                            className="inline-flex items-center justify-center w-24 h-6 text-xs font-semibold rounded-full text-white"
                            style={{
                                backgroundColor: user?.active ? COLORS.ACTIVE : COLORS.MAINTENANCE,
                            }}
                        >
                            {user?.active ? "Active" : "Inactive"}
                        </span>
                    </div>
                </div>

                {/* Address and Franchise */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
                    <Detail
                        label="Address"
                        value={
                            user?.address
                                ? `${user.address.street}, ${user.address.city}, ${user.address.state}, ${user.address.postalCode}`
                                : "No address available"
                        }
                    />
                    <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Franchise</p>
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-900 text-m rounded-full border border-gray-300">
                            {franchiseName || "No franchise"}
                        </span>
                    </div>
                </div>

                {/* Terminals */}
                {terminals.length > 0 && (
                    <div className="mt-4 pt-2 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Allotted Terminals</p>
                        <div className="flex flex-wrap gap-2">
                            {terminals.map((terminal: Terminal, index: number) => (
                                <span
                                    key={index}
                                    className="px-2.5 py-1 bg-gray-100 text-gray-900 text-m rounded-full border border-gray-300"
                                >
                                    {terminal.name || terminal.terminalId || `Terminal ${index + 1}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Password Section */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-xs-bold font-semibold text-gray-800 mb-4">Change Password</h4>

                    <AnimatePresence>
                        {message.text && (
                            <motion.div
                                key="message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className={`mb-4 p-3 rounded-md border ${message.type === "error"
                                    ? "bg-red-50 border-red-200 text-red-700"
                                    : "bg-green-50 border-green-200 text-green-700"
                                    }`}
                            >
                                <p className="text-sm font-medium">{message.text}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-6">
                        <PasswordField
                            label="Current Password"
                            placeholder="Enter current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                        <PasswordField
                            label="New Password"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <PasswordField
                            label="Re-type New Password"
                            placeholder="Re-enter new password"
                            value={retypePassword}
                            onChange={(e) => setRetypePassword(e.target.value)}
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                        <button
                            onClick={handleChangePassword}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                        >
                            {loading ? "Updating..." : "Change Password"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const Detail = ({ label, value }: { label: string, value?: string }) => (
    <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
        <p className="text-base text-gray-900 font-medium">{value || "N/A"}</p>
    </div>
)

const PasswordField = ({
    label,
    placeholder,
    value,
    onChange,
}: {
    label: string
    placeholder: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}) => (
    <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
        <input
            type="password"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 text-sm"
        />
    </div>
)

export default MyAccountPage